import color from 'chalk';
import { MANIFEST_FILE, NGM_DEV_CLI_NAME } from '../../constants';
import type { Manifest } from '../../types';
import { Err, Ok, type Result } from '../blocks/ts/result';
import { parseManifest } from '../manifest';
import { type AzureProviderState, azure } from './azure';
import { type BitBucketProviderState, bitbucket } from './bitbucket';
import { type GitHubProviderState, github } from './github';
import { type GitLabProviderState, gitlab } from './gitlab';
import { http } from './http';
import type { RegistryProvider, RegistryProviderState } from './types';

// export const providers = [github, gitlab, bitbucket, azure, http];
export const providers = [http];

export const selectProvider = (url: string): RegistryProvider | undefined => {
	const provider = providers.find((p) => p.matches(url));

	return provider;
};

export type FetchOptions = {
	token: string;
	userName: string;
	machineId: string;
	/** Override the fetch method. If you are using this in a node environment you will want to pass `node-fetch` */
	fetch?: typeof fetch;
	verbose: (str: string) => void;
};

export const fetchRaw = async (
	state: RegistryProviderState,
	resourcePath: string,
	{ verbose, fetch: f = fetch, token, userName, machineId }: Partial<FetchOptions> = {}
): Promise<Result<string, string>> => {
	const url = await state.provider.resolveRaw(state, resourcePath);

	verbose?.(`Trying to fetch from ${url}`);

	try {
		const headers = new Headers();

		if (state.provider.headers) {
			for (const [key, value] of state.provider.headers().entries()) {
				headers.append(key, value);
			}
		}

		if (state.provider.name === http.name) {
			if (
				token &&
				userName &&
				machineId &&
				state.provider.authHeader &&
				state.provider.extraTokenHeaders
			) {
				const [key, value] = state.provider.authHeader(token);

				headers.append(key, value);
				if (state.provider.name === 'http' && state.provider.extraTokenHeaders) {
					if (!userName) {
						throw new Error('No user name found for http provider');
					}

					if (!machineId) {
						throw new Error('No machine id found for http provider');
					}

					for (const [key, value] of state.provider.extraTokenHeaders({
						token,
						userName,
						machineId
					})) {
						headers.append(key, value);
					}
				} else {
					throw new Error('No auth header found for http provider');
				}
			}
		}

		const response = await f(url, { headers });

		verbose?.(`Got a response from ${url} ${response.status} ${response.statusText}`);

		if (!response.ok) {
			verbose?.(`Error: ${JSON.stringify(await response.json())}`);
			return Err(
				state.provider.formatFetchError(
					state,
					resourcePath,
					`${response.status} ${response.statusText}${
						response.status === 401
							? `\nYou may want to authenticate using "${color.bold(`${NGM_DEV_CLI_NAME} auth`)}"`
							: ''
					}`
				)
			);
		}

		return Ok(await response.text());
	} catch (err) {
		verbose?.(`Error: ${JSON.stringify(err)}`);
		return Err(state.provider.formatFetchError(state, resourcePath, err));
	}
};

export const fetchManifest = async (
	state: RegistryProviderState,
	{ fetch: f = fetch, ...rest }: Partial<FetchOptions> = {}
): Promise<Result<Manifest, string>> => {
	const manifest = await fetchRaw(state, MANIFEST_FILE, { fetch: f, ...rest });

	if (manifest.isErr()) return Err(manifest.unwrapErr());

	return parseManifest(manifest.unwrap());
};

export * from './types';

export {
	github,
	gitlab,
	bitbucket,
	azure,
	http,
	type AzureProviderState,
	type GitHubProviderState,
	type GitLabProviderState,
	type BitBucketProviderState
};
